import React, { useCallback, useEffect, useState } from 'react';
import {
  CustomCollapseCard,
  CustomImage,
  CustomMoneyInput,
  CustomPageHeader,
  CustomTable,
  errorDialog,
  successDialog,
  Text,
  useText,
} from '@src/components';
import { moneyFormat } from '@src/utils/utils';
import { Alert, Button, Col, Radio, Row } from 'antd';
import iconSuccessBlue from '@src/assets/icons/icon-success-blue.svg';
import iconRightArrow from '@src/assets/icons/icon-right-circle.svg';
import '@src/styles/manageMyCards/balance-transfer.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import BalanceTypeFilter from './BalanceTypeFilter';
import {
  addBalanceTransfers,
  setEntryList,
  setExitList,
  setSelectedEntryRow,
  setSelectedEntryRowKeys,
  setSelectedExitRow,
  setSelectedExitRowKeys,
} from '@src/store/slice/balanceTransferSlice';

const columns = [
  {
    title: <Text t="nameUpperCase" />,
    dataIndex: 'name',
    key: 'name',
    width: '10%',
  },
  {
    title: <Text t="surnameUpperCase" />,
    dataIndex: 'surname',
    key: 'surname',
    width: '10%',
  },
  {
    title: <Text t="referenceCode3" />,
    dataIndex: 'refNo',
    key: 'refNo',
    width: '25%',
  },
  {
    title: <Text t="cardNumber4" />,
    dataIndex: 'cardNumber',
    key: 'cardNumber',
    align: 'center',
    width: '25%',
  },
  {
    title: <Text t="balancetl" />,
    dataIndex: 'balance',
    key: 'balance',
    align: 'center',
    width: '20%',
    render: (val) => moneyFormat(val),
  },
];

const BalanceTransfer = () => {
  const isMobil = useMediaQuery({ query: '(max-width: 991.98px)' });
  const isDesktop = useMediaQuery({ query: '(max-width: 1279.98px)' });
  const [transferType, setTransferType] = useState(1);
  const {
    exit,
    entry,
    selectedExitRow,
    selectedExitRowKeys,
    selectedEntryRow,
    selectedEntryRowKeys,
  } = useSelector((state) => state?.balanceTransfer);
  const [isLoading, setIsLoading] = useState(false);
  const [isClear, setIsClear] = useState(false);

  const [amount, setAmount] = useState(0);

  const dispatch = useDispatch();

  const exitRowSelection = (selectedRowKeys, selectedRows) => {
    dispatch(setSelectedExitRow(selectedRows[0]));
    dispatch(setSelectedExitRowKeys(selectedRowKeys));
  };

  const entryRowSelection = (selectedRowKeys, selectedRows) => {
    dispatch(setSelectedEntryRow(selectedRows[0]));
    dispatch(setSelectedEntryRowKeys(selectedRowKeys));
  };

  const onClickTransfer = async () => {
    if (amount <= 0) {
      errorDialog({
        title: <Text t="error" />,
        message: 'Bakiye tutarı sıfırdan büyük olmalıdır!',
      });

      return;
    }

    if (amount > selectedExitRow.balance) {
      errorDialog({
        title: <Text t="error" />,
        message: 'Kartın bakiyesinden yüksek tutar girilemez!',
      });

      return;
    }

    try {
      if (isLoading) return;
      setIsLoading(true);
      const body = {
        exitPersonalCode: selectedExitRow.personalCode,
        exitCardNumber: selectedExitRow.cardNumber,
        entryPersonalCode: selectedEntryRow.personalCode,
        entryCardNumber: selectedEntryRow.cardNumber,
        transferPrice: amount,
        transferType: transferType === 1,
      };

      const result = await dispatch(addBalanceTransfers(body));

      if (addBalanceTransfers.rejected.match(result)) {
        errorDialog({
          title: <Text t="error" />,
          message: `${selectedExitRow.cardNumber} numaralı karttan ${selectedEntryRow.cardNumber} numaralı karta ${amount} TL'lik bakiye aktarımı talebiniz alınamadı.`,
        });
      } else {
        successDialog({
          title: <Text t="success" />,
          message: `${selectedExitRow.cardNumber} numaralı karttan ${selectedEntryRow.cardNumber} numaralı karta ${amount} TL'lik bakiye aktarımı talebiniz alınmıştır.`,
        });
        setIsClear(true);
        setAmount(0);
        resetTable();
      }
    } catch (error) {
      console.error(error)
    } finally { 
      setIsLoading(false);
    }
  };

  const resetTable = useCallback(() => {
    dispatch(setEntryList([]));
    dispatch(setExitList([]));
  }, [dispatch]);

  const onChangeCardType = (e) => {
    setTransferType(e.target.value);
    resetTable();
  };

  useEffect(() => {
    resetTable();
  }, [resetTable]);

  const rowKey = useCallback((record) => record.cardNumber, []);

  return (
    <div className="balance-transfer-card">
      <CustomPageHeader
        data-testid="balance-transfer"
        title={<Text t="balanceTransfer" />}
        routes={['ticketRestaurantHeader', 'manageMyCards']}
        showBreadCrumb
        showHelpButton
      />

      <Alert
        message={
          <div className="alert-message-contain">
            <div className="icon">
              <CustomImage src={iconSuccessBlue} alt="check" />
            </div>
            <span>
              <Text t="balanceTransferSuccess" />
            </span>
          </div>
        }
        banner
        showIcon={false}
        className="custom-alert-warning"
        closable
        style={{ display: 'none' }}
      />

      <CustomCollapseCard
        bodyCenter={!isMobil}
        cardTitle={<Text t="selectTransferType" />}
        cardTitleAlign="center"
      >
        <Radio.Group
          id="ticketRestaurant-manageMyCards-balanceTransfer-draft-type-radioGroup-input-content"
          onChange={(e) => onChangeCardType(e)}
          value={transferType}
          className="draft-type-radio-input-content"
        >
          <Radio
            id="ticketRestaurant-manageMyCards-balanceTransfer-draft-type-radio-input-content-fromClosedCardtoOpenCard"
            data-testid="fromClosedCardtoOpenCard"
            value={0}
          >
            <div className="new-order-draft">
              <Text t="fromClosedCardtoOpenCard" />
            </div>
          </Radio>
          <Radio
            id="ticketRestaurant-manageMyCards-balanceTransfer-draft-type-radio-input-content-openCardToOpenCard"
            data-testid="openCardToOpenCard"
            value={1}
          >
            <div className="balance-upload-draft">
              <Text t="openCardToOpenCard" />
            </div>
          </Radio>
        </Radio.Group>
      </CustomCollapseCard>

      <Row gutter={24}>
        <Col span={isMobil ? 24 : 12}>
          <div className="custom-card-header">
            <div className="title">
              <Text t="cardInformationForTransferOut" />
            </div>
          </div>
          <div className="custom-card-body">
            <BalanceTypeFilter
              isClear={isClear}
              setIsClear={setIsClear}
              type="exit"
              statusId={transferType === 0 ? 16 : 13}
            />
            <CustomTable
              pagination={false}
              dataSource={exit.list}
              rowSelection={{
                columnTitle: useText('selectUpperCase'),
                type: 'radio',
                columnWidth: '10%',
                onChange: exitRowSelection,
                selectedRowKeys: selectedExitRowKeys,
              }}
              columns={columns}
              rowKey={rowKey}
              scroll={isDesktop ? { x: 992 } : null}
            />
          </div>
        </Col>
        <Col span={isMobil ? 24 : 12} style={{ marginTop: isMobil ? 24 : 0 }}>
          <div className="custom-card-header">
            <div className="title">
              <Text t="cardInformationForTransferIn" />
            </div>
          </div>
          <div className="custom-card-body">
            <BalanceTypeFilter
              isClear={isClear}
              setIsClear={setIsClear}
              type="entry"
              statusId={13}
            />
            <CustomTable
              width="100%"
              pagination={false}
              dataSource={entry.list}
              rowSelection={{
                columnTitle: useText('selectUpperCase'),
                type: 'radio',
                columnWidth: '10%',
                onChange: entryRowSelection,
                selectedRowKeys: selectedEntryRowKeys,
              }}
              columns={columns}
              rowKey={rowKey}
              scroll={isDesktop ? { x: 992 } : null}
            />
          </div>
        </Col>
      </Row>

      <div className="step-btn-container">
        <div className="transfer">
          <span className="transfer-text">
            <Text t="transferAmount" />
            <span>:</span>
          </span>

          <CustomMoneyInput
            id="ticketRestaurant-manageMyCards-balanceTransfer-main-amount-moneyInput"
            onChange={(val) => {
              setAmount(val);
            }}
            value={amount}
            placeholder="0,00"
            addonAfter="₺"
            height={36}
            data-testid="money"
          />
        </div>

        <Button
          id="manageMyCards-balanceTransfer-main-next-btn"
          data-testid="transfer"
          onClick={onClickTransfer}
          disabled={!selectedExitRow || !selectedEntryRow}
          className="next-btn"
        >
          <CustomImage style={{ marginRight: 16 }} src={iconRightArrow} alt="check" />
          <Text t="makeBalanceTransfer" />
        </Button>
      </div>
    </div>
  );
};

export default BalanceTransfer;
